import React, { useEffect, useRef, useState } from 'react';
import PhysicsLayout from './PhysicsLayout';

const g = 9.81;

const ForcesBench = () => {
  const [m, setM] = useState(12);
  const [F, setF] = useState(80);
  const [theta, setTheta] = useState(15);
  const [mu, setMu] = useState(0.35);
  const canvasRef = useRef(null);
  const xRef = useRef(40);

  const rad = (theta * Math.PI) / 180;
  const N = Math.max(0, m * g - F * Math.sin(rad));
  const drive = F * Math.cos(rad);
  const friction = mu * N;
  const a = (drive - friction) / m;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;
    let last = performance.now();
    const draw = (now) => {
      const dt = Math.min(0.04, (now - last) / 1000);
      last = now;
      const w = canvas.width;
      const h = canvas.height;
      if (a > 0.05) xRef.current = Math.min(w - 90, xRef.current + a * 18 * dt);
      else if (a < -0.05) xRef.current = Math.max(30, xRef.current + a * 18 * dt);
      ctx.fillStyle = '#0b1020';
      ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = 'rgba(167,139,250,0.25)';
      ctx.beginPath();
      ctx.moveTo(16, h - 48);
      ctx.lineTo(w - 16, h - 48);
      ctx.stroke();
      const x = xRef.current;
      const y = h - 88;
      ctx.fillStyle = '#a78bfa';
      ctx.fillRect(x, y, 56, 40);
      ctx.strokeStyle = '#c4b5fd';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x + 56, y + 20);
      const L = 40 + F * 0.4;
      ctx.lineTo(x + 56 + L * Math.cos(rad), y + 20 - L * Math.sin(rad));
      ctx.stroke();
      ctx.fillStyle = '#94a3b8';
      ctx.font = '12px Inter, sans-serif';
      ctx.fillText(a > 0.05 ? 'moving' : a < -0.05 ? 'sliding back' : 'stuck', x, y - 10);
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [a, F, rad]);

  return (
    <PhysicsLayout
      kicker="Bench 1 · every engineer"
      title="Forces on the floor"
      lead="A crate, a pull, a dirty floor. If the horizontal component does not beat friction, nothing happens — which is most of real work."
    >
      <div className="pp-split">
        <div>
          <label className="pp-field">Mass {m} kg
            <input type="range" min="2" max="40" value={m} onChange={(e) => setM(+e.target.value)} />
          </label>
          <label className="pp-field">Pull {F} N
            <input type="range" min="0" max="250" value={F} onChange={(e) => setF(+e.target.value)} />
          </label>
          <label className="pp-field">Angle {theta}°
            <input type="range" min="0" max="70" value={theta} onChange={(e) => setTheta(+e.target.value)} />
          </label>
          <label className="pp-field">μ {mu.toFixed(2)}
            <input type="range" min="0" max="1" step="0.01" value={mu} onChange={(e) => setMu(+e.target.value)} />
          </label>
        </div>
        <div>
          <div className="pp-stage">
            <canvas ref={canvasRef} width={520} height={260} />
          </div>
          <div className="pp-readout" style={{ marginTop: '0.8rem' }}>
            <p className="pp-eq">a = (F cosθ − μN) / m</p>
            <strong>{a.toFixed(2)} m/s²</strong>
            <p className="pp-note">N = {N.toFixed(0)} N · friction {friction.toFixed(0)} N · drive {drive.toFixed(0)} N. Pulling up unloads the floor and can help — until you waste the force vertically.</p>
          </div>
        </div>
      </div>
    </PhysicsLayout>
  );
};

export default ForcesBench;
